import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/hooks/use-data';
import { complaintService } from '@/services/complaint-service';

/**
 * Complaints against units and the board-prep report (#566).
 *
 * Keys sit under `complaints`, so one invalidation refreshes the pane's target list and
 * each lot's "complaints against" section; the report lives under `applications` — see
 * `boardPrepAll`.
 */
export const complaintKeys = {
	all: ['complaints'] as const,
	targets: (messageId: string) => [...complaintKeys.all, 'targets', messageId] as const,
	against: (accountId: string) => [...complaintKeys.all, 'against', accountId] as const,
	/**
	 * Deliberately under the APPLICATIONS branch, not `complaints`: half the report is
	 * improvement requests, and every application mutation already invalidates
	 * `queryKeys.applications.all`, so approving a request from the report refreshes it with
	 * no per-mutation wiring. The complaint side invalidates it explicitly (below, and in
	 * `invalidateMessagesAndApplicationCounts`).
	 */
	//
	// A LITERAL, not `[...queryKeys.applications.all, …]`: this runs at import, and the
	// component specs that mock `@/hooks/use-data` have no `queryKeys` — reading it here
	// failed every one of them at load. The hook spec pins this prefix to the real key.
	boardPrepAll: ['applications', 'board-prep'] as const,
	boardPrep: (associationId: string) => [...complaintKeys.boardPrepAll, associationId] as const,
};

/**
 * After an ownership transfer: `applyOwnershipTransfer` moves open complaints' targets to
 * the buyer's account in SQL, so every cached "complaints against" list and board-prep
 * report may now name the wrong owner. Called by both transfer entry points (the account
 * wizard and the review-queue apply).
 */
export function invalidateComplaintsAfterTransfer(queryClient: ReturnType<typeof useQueryClient>): void {
	void queryClient.invalidateQueries({ queryKey: complaintKeys.all });
	void queryClient.invalidateQueries({ queryKey: complaintKeys.boardPrepAll });
}

/** The units one complaint is against. */
export function useComplaintTargets(messageId: string, options: { enabled?: boolean } = {}) {
	return useQuery({
		queryKey: complaintKeys.targets(messageId),
		queryFn: () => complaintService.getTargets(messageId),
		enabled: (options.enabled ?? true) && !!messageId,
	});
}

/** Add or remove a unit on a complaint. */
export function useSetComplaintTarget() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ messageId, accountId, on }: { messageId: string; accountId: string; on: boolean }) =>
			(on ? complaintService.addTarget(messageId, accountId) : complaintService.removeTarget(messageId, accountId)),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: complaintKeys.all });
			queryClient.invalidateQueries({ queryKey: complaintKeys.boardPrepAll });
		},
	});
}

/**
 * Mark a complaint reported to the board (or not). Invalidates the messages branch too:
 * the pane reads `reportedToBoardAt` off the message detail.
 */
export function useSetReportedToBoard() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ messageId, reported }: { messageId: string; reported: boolean }) =>
			complaintService.setReportedToBoard(messageId, reported),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: complaintKeys.all });
			queryClient.invalidateQueries({ queryKey: complaintKeys.boardPrepAll });
			queryClient.invalidateQueries({ queryKey: queryKeys.messages.all });
		},
	});
}

/** Complaints that name this lot as a target. */
export function useComplaintsAgainst(accountId: string, options: { enabled?: boolean } = {}) {
	return useQuery({
		queryKey: complaintKeys.against(accountId),
		queryFn: () => complaintService.getComplaintsAgainst(accountId),
		enabled: (options.enabled ?? true) && !!accountId,
	});
}

/** The per-association board-meeting prep report. */
export function useBoardPrepReport(associationId: string) {
	return useQuery({
		queryKey: complaintKeys.boardPrep(associationId),
		queryFn: () => complaintService.getBoardPrep(associationId),
		enabled: !!associationId,
	});
}
